import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import api, { clearAuthToken } from '@/lib/api';
import { toast } from 'sonner';
import { Users, CheckCircle, AlertCircle, ArrowRight, User, LogOut } from 'lucide-react';

export default function PlayerOnboarding() {
  const [joinCode, setJoinCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [team, setTeam] = useState(null);
  const [fanProfile, setFanProfile] = useState(null); // For fan-created profiles
  const [unclaimedProfiles, setUnclaimedProfiles] = useState([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showFanProfileModal, setShowFanProfileModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [claimingProfile, setClaimingProfile] = useState(false);
  const navigate = useNavigate();

  const handleLookupTeam = async (e) => {
    e.preventDefault();
    
    if (joinCode.length !== 8) {
      toast.error('Please enter an 8-character join code');
      return;
    }

    setLookupLoading(true);
    setTeam(null);
    setFanProfile(null);

    try {
      // Lookup the team or fan profile by join code
      const lookupResponse = await api.get(`/teams/lookup/${joinCode.toUpperCase()}`);
      const data = lookupResponse.data;

      if (data.type === 'fan_player') {
        // This is a fan-created player profile
        if (data.already_claimed) {
          toast.error('This profile has already been claimed by another player.');
          return;
        }
        setFanProfile(data);
        setShowFanProfileModal(true);
      } else if (data.type === 'team') {
        // This is a team join code
        setTeam(data);
        // Fetch unclaimed profiles for this team
        const profilesResponse = await api.get(`/teams/${data.team_id}/unclaimed-profiles`);
        setUnclaimedProfiles(profilesResponse.data);
        setShowProfileModal(true);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Invalid join code. Please check and try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to lookup code');
      }
      console.error(error);
    } finally {
      setLookupLoading(false);
    }
  };

  const handleClaimFanProfile = async () => {
    if (!fanProfile) return;

    setClaimingProfile(true);

    try {
      await api.post('/profiles/claim-fan', {
        player_id: fanProfile.player_id
      });

      toast.success(`Successfully claimed your profile! Stats from ${fanProfile.team_name} are now linked.`);
      setShowFanProfileModal(false);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim profile');
      console.error(error);
    } finally {
      setClaimingProfile(false);
    }
  };

  const handleClaimProfile = async () => {
    if (!selectedProfile || !team) return;

    setClaimingProfile(true);

    try {
      await api.post('/profiles/claim', {
        player_id: selectedProfile.player_id,
        team_id: team.team_id
      });

      toast.success(`Successfully joined ${team.name}!`);
      setShowProfileModal(false);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim profile');
      console.error(error);
    } finally {
      setClaimingProfile(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 p-4">
      <div className="max-w-2xl mx-auto py-12">
        <div className="text-center mb-8">
          <div className="inline-block p-4 bg-orange-600 rounded-full mb-4">
            <Users className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase font-heading text-slate-900 mb-2">
            Join Your Team
          </h1>
          <p className="text-lg text-slate-600">Enter your join code to find and claim your profile</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg shadow-md p-8">
          <form onSubmit={handleLookupTeam} className="space-y-6">
            <div>
              <Label htmlFor="joinCode" className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                Join Code
              </Label>
              <Input
                id="joinCode"
                data-testid="join-code-input"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="Enter 8-character code"
                maxLength={8}
                required
                className="h-14 text-center text-2xl font-bold tracking-widest border-2 bg-white border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 uppercase"
              />
              <p className="text-sm text-slate-500 mt-2 text-center">
                Ask your coach or parent for the join code
              </p>
            </div>

            <Button
              type="submit"
              disabled={lookupLoading || joinCode.length !== 8}
              data-testid="find-team-button"
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wide shadow-md hover:shadow-lg transition-all active:scale-95 disabled:opacity-50"
            >
              {lookupLoading ? (
                <span className="flex items-center justify-center">
                  <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                  Finding Team...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  Find My Team
                  <ArrowRight className="w-5 h-5 ml-2" />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <button
              onClick={async () => {
                try { await api.post('/auth/logout', {}); } catch {}
                clearAuthToken();
                navigate('/login');
              }}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors"
              style={{ background: 'none', border: 'none' }}
              data-testid="player-onboarding-logout"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Profile Selection Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-2xl text-center">
              Select Your Profile
            </DialogTitle>
            <DialogDescription className="text-slate-600 text-center mt-1">
              {team ? `${team.name}${team.level ? ` • ${team.level}` : ''}` : 'Select your profile from the team roster'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4">
            {unclaimedProfiles.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-slate-900 mb-2">No Available Profiles</h3>
                <p className="text-slate-600 text-sm">
                  Your coach hasn't added your profile yet, or all profiles have been claimed.
                  <br />
                  Please contact your coach to add you to the roster.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-slate-600 mb-4 text-center">
                  Select your name from the roster below
                </p>
                {unclaimedProfiles.map((profile) => (
                  <button
                    key={profile.player_id}
                    onClick={() => setSelectedProfile(profile)}
                    data-testid={`profile-${profile.player_id}`}
                    className={`w-full p-4 rounded-lg border-2 transition-all text-left flex items-center justify-between ${
                      selectedProfile?.player_id === profile.player_id
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                        selectedProfile?.player_id === profile.player_id
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-100 text-slate-700'
                      }`}>
                        {profile.jersey_number || '#'}
                      </div>
                      <div>
                        <p className="font-bold text-lg text-slate-900">{profile.name}</p>
                        <p className="text-sm text-slate-500">
                          Jersey #{profile.jersey_number || 'Not assigned'}
                        </p>
                      </div>
                    </div>
                    {selectedProfile?.player_id === profile.player_id && (
                      <CheckCircle className="w-6 h-6 text-blue-600" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {unclaimedProfiles.length > 0 && (
            <div className="pt-4 border-t border-slate-200">
              <Button
                onClick={handleClaimProfile}
                disabled={!selectedProfile || claimingProfile}
                data-testid="claim-profile-button"
                className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wide disabled:opacity-50"
              >
                {claimingProfile ? (
                  <span className="flex items-center justify-center">
                    <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                    Claiming Profile...
                  </span>
                ) : (
                  'Claim This Profile'
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Fan Profile Claim Modal */}
      <Dialog open={showFanProfileModal} onOpenChange={setShowFanProfileModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-2xl text-center">
              Claim Your Profile
            </DialogTitle>
            <DialogDescription className="text-slate-600 text-center mt-1">
              A fan has been tracking stats for you
            </DialogDescription>
          </DialogHeader>

          {fanProfile && (
            <div className="py-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 text-center mb-6">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-1">{fanProfile.name}</h3>
                <p className="text-slate-600">{fanProfile.team_name}</p>
                <p className="text-sm text-slate-500">{fanProfile.season}</p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-amber-800">
                  <strong>What happens when you claim:</strong>
                  <br />
                  All the game stats tracked for this profile will be linked to your account.
                  You'll be able to see your complete performance history.
                </p>
              </div>

              <Button
                onClick={handleClaimFanProfile}
                disabled={claimingProfile}
                data-testid="claim-fan-profile-button"
                className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white font-bold uppercase tracking-wide disabled:opacity-50"
              >
                {claimingProfile ? (
                  <span className="flex items-center justify-center">
                    <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                    Claiming Profile...
                  </span>
                ) : (
                  'Claim This Profile'
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
